import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import _sortBy from 'lodash/sortBy';
import { ptToPx } from 'paper/utils/helpers';
import type {
  WCardTemplatePageView,
  WCardTemplateElementView,
  WCardSuiteSummaryView,
} from '@zola/svc-web-api-ts-client';
import type { CustomTemplateSummary } from 'cards/components/common/Samples/util/types';
import type { DefaultCropTypes } from './util/types';

import ImageElement from './components/ImageElement/ImageElement';
import TextElement from './components/TextElement/TextElement';
import styles from './SamplePreview.module.less';

export interface SamplePreviewProps {
  templatePage: WCardTemplatePageView;
  defaultCropType?: DefaultCropTypes;
  photoURL?: string;
  uploadcareFile?: { cdnUrl: string };
  isRounded: boolean;
  /** Length of card inside white frame (longer edge); also pass className to change width/height */
  templateSize?: number;
  className?: string;
  customPreviewTemplates?: CustomTemplateSummary[];
  enableFrame: boolean;
  isFolded?: boolean;
  showHand?: boolean;
  activeSuite?: WCardSuiteSummaryView;
}

interface ElementProps {
  element: WCardTemplateElementView;
  index: number;
}

const SamplePreview: React.FC<SamplePreviewProps> = ({
  templatePage,
  defaultCropType = 'cover',
  photoURL,
  uploadcareFile,
  isRounded,
  templateSize = 164,
  className,
  customPreviewTemplates,
  enableFrame = true,
  isFolded = false,
  showHand,
}) => {
  const orderedElements = _sortBy(templatePage.elements, e => e.layer);
  const customImageIndex = orderedElements.findIndex(
    element => element.content_type === 'IMAGE' && element.customizable
  );

  const [customElement, setCustomElement] = useState<WCardTemplateElementView>();

  useEffect(() => {
    setCustomElement(orderedElements[customImageIndex]);

    // Only change custom element when set of template previews changes (not between colors of same template)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPreviewTemplates, orderedElements]);

  const PreviewElement: React.FC<ElementProps> = ({ element, index }) => {
    switch (element.content_type) {
      case 'IMAGE': {
        // Custom image element rendered separately so not re-rendered every time template color changes, avoid flashing content
        if (customImageIndex === index) {
          return null;
        }
        return (
          <ImageElement key={index} element={element} isRounded={isRounded} isFolded={isFolded} />
        );
      }
      case 'TEXT': {
        return <TextElement key={index} element={element} />;
      }
      default: {
        return null;
      }
    }
  };

  const { width: widthPt = 0, height: heightPt = 0 } = templatePage || {};
  let previewWidth = 0;
  const inlinePreviewMargins: React.CSSProperties = {};
  const cardOrientation = widthPt > (isFolded ? heightPt / 2 : heightPt) ? 'landscape' : 'portrait';
  const isLandscape = cardOrientation === 'landscape';
  const widthPx = ptToPx(widthPt);
  const heightPx = ptToPx(heightPt);
  if (isLandscape) {
    previewWidth = templateSize;
    const previewHeight = (heightPx / widthPx) * templateSize;
    inlinePreviewMargins.marginTop = isFolded
      ? `${(templateSize - heightPx) / 4 + 30}px`
      : `${(templateSize - previewHeight) / 2}px`;
  } else {
    previewWidth = (widthPx / heightPx) * templateSize;
    inlinePreviewMargins.marginLeft = `${(templateSize - previewWidth) / 2}px`;
  }
  const scaleRatio = previewWidth / widthPx;
  const transform = `scale(${scaleRatio})`;

  const borderStyles = {
    width: `${widthPt}pt`,
    height: `${heightPt}pt`,
  };

  const left = isLandscape ? '-52px' : '-29px';

  return (
    <div
      className={cx(styles.previewContainer, className, {
        [styles.previewContainerDefaultSize]: !className,
        [styles.withFrame]: enableFrame,
      })}
    >
      <div
        className={cx(styles.canvasBoxContainer, {
          [styles.trapezoid]: isFolded,
        })}
        style={inlinePreviewMargins}
      >
        {showHand && <div className={styles.previewHand} style={{ left }} />}
        <div className={styles.canvasBox} style={{ transform }}>
          {orderedElements.map((element, i) => (
            <PreviewElement key={i} element={element} index={i} />
          ))}
          {customElement && (
            <ImageElement
              element={customElement}
              defaultCropType={defaultCropType}
              photoURL={photoURL}
              uploadcareFile={uploadcareFile}
              isRounded={isRounded}
              isFolded={isFolded}
            />
          )}
          <div className={styles.border} style={borderStyles} />
        </div>
      </div>
      {isFolded && <div className={styles.foldedBg} />}
    </div>
  );
};

export default SamplePreview;
